import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  DocumentPublishState,
  DocumentSortField,
  DocumentSourceType,
  DocumentStatus,
} from '../../../types/knowledge';
import {
  buildDocumentListOptions,
  buildDocumentSearchParams,
  DEFAULT_DOCUMENT_FILTER,
  DEFAULT_DOCUMENT_SORT,
  DOCUMENT_SEARCH_DEBOUNCE_MS,
  DOCUMENT_SEARCH_MIN_LENGTH,
  getNextDocumentSort,
  parseDocumentFilterFromSearchParams,
  parseDocumentPageFromSearchParams,
  parseDocumentSortFromSearchParams,
  type DocumentFilterState,
  type DocumentSortState,
} from '../documentListParams';
import type { DocumentPageSize } from '../../../types/userConfig';

export function useDocumentListSearchParams(
  pageSize: DocumentPageSize,
  publishState?: DocumentPublishState,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchDebounceTimeoutRef = useRef<number | null>(null);
  const documentSortRef = useRef(DEFAULT_DOCUMENT_SORT);
  const documentFilterRef = useRef(DEFAULT_DOCUMENT_FILTER);

  const urlSearchValue = searchParams.get('search') ?? '';
  const [documentSearchInput, setDocumentSearchInput] =
    useState(urlSearchValue);
  const [previousUrlSearchValue, setPreviousUrlSearchValue] =
    useState(urlSearchValue);

  if (urlSearchValue !== previousUrlSearchValue) {
    setPreviousUrlSearchValue(urlSearchValue);
    setDocumentSearchInput(urlSearchValue);
  }

  const documentSort = useMemo(
    () => parseDocumentSortFromSearchParams(searchParams),
    [searchParams],
  );
  const documentFilter = useMemo(
    () => parseDocumentFilterFromSearchParams(searchParams),
    [searchParams],
  );
  const documentPage = useMemo(
    () => parseDocumentPageFromSearchParams(searchParams),
    [searchParams],
  );

  useEffect(() => {
    documentSortRef.current = documentSort;
    documentFilterRef.current = documentFilter;
  }, [documentFilter, documentSort]);

  useEffect(() => {
    return () => {
      if (searchDebounceTimeoutRef.current !== null) {
        window.clearTimeout(searchDebounceTimeoutRef.current);
      }
    };
  }, []);

  const replaceParams = useCallback(
    ({
      sort,
      filter,
      page,
      searchInput,
    }: {
      sort: DocumentSortState;
      filter: DocumentFilterState;
      page: number;
      searchInput: string;
    }) => {
      setSearchParams(
        buildDocumentSearchParams({
          sort,
          filter,
          page,
          searchInput,
        }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleDocumentSearchChange = useCallback(
    (value: string) => {
      setDocumentSearchInput(value);

      if (searchDebounceTimeoutRef.current !== null) {
        window.clearTimeout(searchDebounceTimeoutRef.current);
        searchDebounceTimeoutRef.current = null;
      }

      if (value.trim() === '') {
        replaceParams({
          sort: documentSortRef.current,
          filter: { ...documentFilterRef.current, debouncedFilename: '' },
          page: 1,
          searchInput: '',
        });
        return;
      }

      searchDebounceTimeoutRef.current = window.setTimeout(() => {
        const trimmed = value.trim();
        replaceParams({
          sort: documentSortRef.current,
          filter: {
            ...documentFilterRef.current,
            debouncedFilename:
              trimmed.length >= DOCUMENT_SEARCH_MIN_LENGTH ? trimmed : '',
          },
          page: 1,
          searchInput: value,
        });
        searchDebounceTimeoutRef.current = null;
      }, DOCUMENT_SEARCH_DEBOUNCE_MS);
    },
    [replaceParams],
  );

  const handleDocumentSortToggle = useCallback(
    (column: DocumentSortField) => {
      replaceParams({
        sort: getNextDocumentSort(documentSort, column),
        filter: documentFilter,
        page: 1,
        searchInput: documentSearchInput,
      });
    },
    [documentFilter, documentSearchInput, documentSort, replaceParams],
  );

  const handleSourceFilterChange = useCallback(
    (value: string) => {
      replaceParams({
        sort: documentSort,
        filter: {
          ...documentFilter,
          source: value === 'all' ? null : (value as DocumentSourceType),
        },
        page: 1,
        searchInput: documentSearchInput,
      });
    },
    [documentFilter, documentSearchInput, documentSort, replaceParams],
  );

  const handleStatusFilterChange = useCallback(
    (value: string) => {
      replaceParams({
        sort: documentSort,
        filter: {
          ...documentFilter,
          status: value === 'all' ? null : (value as DocumentStatus),
        },
        page: 1,
        searchInput: documentSearchInput,
      });
    },
    [documentFilter, documentSearchInput, documentSort, replaceParams],
  );

  const setDocumentPage = useCallback(
    (page: number) => {
      replaceParams({
        sort: documentSort,
        filter: documentFilter,
        page,
        searchInput: documentSearchInput,
      });
    },
    [documentFilter, documentSearchInput, documentSort, replaceParams],
  );

  const resetDocumentListParams = useCallback(() => {
    setDocumentSearchInput('');
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const listOptions = useMemo(
    () =>
      buildDocumentListOptions(
        documentSort,
        documentFilter,
        documentPage,
        pageSize,
        publishState,
      ),
    [documentFilter, documentPage, documentSort, pageSize, publishState],
  );

  const sourceFilterValue = documentFilter.source ?? 'all';
  const statusFilterValue = documentFilter.status ?? 'all';

  const hasActiveDocumentFilters =
    documentFilter.debouncedFilename.length >= DOCUMENT_SEARCH_MIN_LENGTH ||
    documentFilter.source !== null ||
    documentFilter.status !== null;

  return {
    documentSort,
    documentFilter,
    documentPage,
    documentSearchInput,
    sourceFilterValue,
    statusFilterValue,
    listOptions,
    hasActiveDocumentFilters,
    handleDocumentSearchChange,
    handleDocumentSortToggle,
    handleSourceFilterChange,
    handleStatusFilterChange,
    setDocumentPage,
    resetDocumentListParams,
    DEFAULT_DOCUMENT_SORT,
    DEFAULT_DOCUMENT_FILTER,
  };
}
